import { LineBasicMaterial, Object3D, Raycaster, Vector3, Plane } from "three";

import { mmFromSceneUnits, sceneUnitsFromMm } from "../utils/scaler";
import ICanvasInputListener from "../logic/ICanvasInputListener";
import LengthDimensionData from "../model/sceneObjectData/LengthDimensionData";
import LineSegment3d from "../math/linearAlgebra/lineSegment3d";
import GeometryCommand from "./GeometryCommand";
import CommandIdEnum from "../enums/CommandIdEnums";
import CanvasInputListenerModeEnum from "../enums/CanvasInputListenerModeEnum";
import { useViewerStateStore } from "../store/viewerStateStore";
import { NavigationModeEnum } from "../store/viewerState/ViewerMode/ViewerMode";
import RaycastIntersection from "../logic/RaycastIntersection";
import { getPerpendicularToLine } from "../utils/geometryUtils";
import createSceneObject from "../model/sceneObject/SceneObjectFactory";
import ISceneObject from "../model/sceneObject/ISceneObject";
import Document from "../model/document/Document";
import CommandStack from "../logic/CommandStack";
import { useModelPropertiesStore } from "../store/modelPropertiesStore/modelPropertiesStore";
import { tolerances } from "../utils/constants";
import { useUserStateStore } from "../store/userStateStore";

enum AddLengthDimensionState {
    SETTING_FIRST_PT, // No points set
    SETTING_SECOND_PT, // First point set
    SETTING_OFFSET, // Second point set
    ENTERING_TEXT, // Offset set
    FINISHED
}

//const offsetRecoil = sceneUnitsFromMm(10); // Distance from dimension line and text to target plane

class AddLengthDimensionCmd extends GeometryCommand {

    cmdState: AddLengthDimensionState = AddLengthDimensionState.SETTING_FIRST_PT; // State of the command
    lengthDimensionDto: LengthDimensionData | undefined;

    drawablesMaterial: LineBasicMaterial = new LineBasicMaterial({ color: 0xff0000 });
    sceneObject: ISceneObject | null = null; // Reference to the scene object

    constructor(    commandStack: CommandStack,
                    canvasInputListener: ICanvasInputListener,
                    document: Document) {
        super(CommandIdEnum.AddLengthDimension, commandStack, canvasInputListener, document ); // Call parent constructor
        this.commandNameResourceKey = 'command.addLengthDimension.title';
        this.initialPrompResourceKey = 'command.addLengthDimension.selectFirstPoint';
    }

    start() {
        super.start();
        super.addToStack();
        this.raycastRecoil = sceneUnitsFromMm(10);
        this.activate();
    }

    activate() {
        this.connectMouse();
    }

    connectMouse() {
        super.connectMouse();
        this.canvasInputListener.setRaycastMode(CanvasInputListenerModeEnum.RAYCAST_TARGET_OBJECTS);
        this.canvasInputListener.resetTargetObjects();
    }

    deactivate() {
        this.disconnectMouse();
    }

    getObject() {
        if (!this.sceneObject) {
            if (!this.lengthDimensionDto) {
                console.error("LengthDimensionDto is not set, cannot create scene object.");
                return null;
            }

            this.sceneObject = createSceneObject(this.lengthDimensionDto); // Create a new scene object with the DTO
            this.sceneObject.onAnnotationDirectionChange(useModelPropertiesStore.getState().rotationOffset); // Update the orientation of the annotation text
            this.addToDrawables(this.sceneObject); // Add the scene object to the drawables
        }      
        
        return this.sceneObject; // Return the scene object
    }

    point(raycastResult: RaycastIntersection): boolean {

        const offsetPt = this.getRefLineAlignedPoint(raycastResult.point.clone()); // Get the point from the raycast result
        
        switch (this.cmdState) {
            case AddLengthDimensionState.SETTING_FIRST_PT:  
                super.setParent(raycastResult.object); // Set the parent object from the raycast result
                this.addRecoil(offsetPt); // Add recoil to the point
        
                if (useViewerStateStore.getState().viewerMode?.navigationMode === NavigationModeEnum.AlongThePlane) {
                    this.canvasInputListener.setRaycastMode(CanvasInputListenerModeEnum.RAYCAST_PLANE);
                    this.canvasInputListener.setTargetPlaneFromCamera(offsetPt.clone());
                }
                else {
                    const normalToUse = this.planeGridHelper.activeNormal || raycastResult.normalInWcs;
                    if (normalToUse) {
                        this.canvasInputListener.setRaycastMode(CanvasInputListenerModeEnum.RAYCAST_PLANE);
                        this.canvasInputListener.setTargetPlane(new Plane().setFromNormalAndCoplanarPoint(   
                                                                            normalToUse.clone(),
                                                                            offsetPt.clone()));
                    }
                    else {
                        this.canvasInputListener.setRaycastMode(CanvasInputListenerModeEnum.RAYCAST_TARGET_OBJECTS);
                    }
                }

                this.lengthDimensionDto = new LengthDimensionData(offsetPt.clone(), offsetPt.clone(), undefined, "- mm");
                
                //console.info("First point set: ", this.lengthDimensionDto.start);
                this.cmdState = AddLengthDimensionState.SETTING_SECOND_PT; 
                this.showPlaneGrid(offsetPt.clone(), null);
                this.showSwitchPlaneButton();
                this.setReferencePoint(offsetPt.clone());
                this.enableReferenceLine();
                this.startMeasure(offsetPt.clone()); 
                this.showInfoMessage('command.addLengthDimension.selectSecondPoint');
            break;

            case AddLengthDimensionState.SETTING_SECOND_PT:
                    if (this.lengthDimensionDto) {
                        this.lengthDimensionDto.end = offsetPt;
                        
                        //console.info("Second point set: ", this.lengthDimensionDto?.end);
                        this.cmdState = AddLengthDimensionState.SETTING_OFFSET; 
                        this.disableReferenceLine();

                        this.stopMeasure();
                        this.showInfoMessage('command.addLengthDimension.setOffset');
                }
                break;

            case AddLengthDimensionState.SETTING_OFFSET:
                if (this.lengthDimensionDto) {

                    const perpendicular = getPerpendicularToLine(   this.lengthDimensionDto.start, 
                                                                    this.lengthDimensionDto.end, 
                                                                    offsetPt);
                    
                    if (perpendicular.lengthSq() < tolerances.position) {
                        return true;
                    }

                    this.lengthDimensionDto.offset = perpendicular.clone(); // Update the offset point
         
                    // Save to document
                    if (!this.persistEntity(this.lengthDimensionDto)) {
                        console.error("Failed to save length dimension. Please try again.");
                        alert("Failed to save length dimension. Please try again.");
                        return true;
                    }

                    this.end();
                }
                //this.cmdState = AddLengthDimensionState.ENTERING_TEXT; 
                break;

            case AddLengthDimensionState.ENTERING_TEXT:
                this.end();
                break;

            default:
                console.error("Invalid command state for point: ", AddLengthDimensionState[this.cmdState]);
                break;
        }

        return true;
    }

    end() {
        this.deactivate();
        super.end();
        super.removeFromStack();
        this.lengthDimensionDto = undefined;
        this.sceneObject = null;
    }

    onSwitchPlane(): void {
        super.onSwitchPlane();
        if (!this.lengthDimensionDto) return;
        const normalToUse = this.planeGridHelper.activeNormal;
        if (!normalToUse) return;

        if (this.cmdState === AddLengthDimensionState.SETTING_SECOND_PT) {
            this.canvasInputListener.setTargetPlane(
                new Plane().setFromNormalAndCoplanarPoint(normalToUse.clone(), this.lengthDimensionDto.start.clone())
            );
        } else if (this.cmdState === AddLengthDimensionState.SETTING_OFFSET) {
            this.canvasInputListener.setTargetPlane(
                new Plane().setFromNormalAndCoplanarPoint(normalToUse.clone(), this.lengthDimensionDto.end.clone())
            );
        }
    }

    rightClick(event: MouseEvent, raycaster: Raycaster, targets: Object3D[]) {
        this.onSwitchPlane();
    }

    pointerMove(raycastResult: RaycastIntersection): boolean {
        
        if (this.cmdState === AddLengthDimensionState.SETTING_FIRST_PT) {
            if (raycastResult && (raycastResult.normalInWcs || this.planeGridHelper.activeNormal)) {
                this.showPlaneGrid(raycastResult.point, raycastResult.normalInWcs);
            } else {
                this.hidePlaneGrid();
            }
        } else if (this.cmdState === AddLengthDimensionState.SETTING_SECOND_PT && this.lengthDimensionDto) {
            this.showPlaneGrid(this.lengthDimensionDto.start, null);
        }

        if (!raycastResult) return true;

        super.pointerMove(raycastResult); // Call the parent method to handle pointer move        

        if (!this.lengthDimensionDto)
            return true;

        const point = this.getRefLineAlignedPoint(raycastResult.point.clone()); 

        this.updateRefLine(point);

        switch (this.cmdState) {
            case AddLengthDimensionState.SETTING_SECOND_PT:
                this.lengthDimensionDto.end = point.clone();
                this.lengthDimensionDto.text = this.getDisplayText();
                break;

            case AddLengthDimensionState.SETTING_OFFSET:
                const perpendicular = getPerpendicularToLine(   this.lengthDimensionDto.start, 
                                                                this.lengthDimensionDto.end, 
                                                                point);
                this.lengthDimensionDto.offset = perpendicular.clone(); // Update the offset point
                break;
        }

        this.getObject()?.geometry.update(this.lengthDimensionDto, false); // Update the geometry with the new end point

        return true;
    }


    getDisplayText(): string {
        if (this.lengthDimensionDto) {

            const length = new LineSegment3d(this.lengthDimensionDto.start, this.lengthDimensionDto.end).length();
            const lengthInCm = mmFromSceneUnits(length) / 10; // Convert to cm

            return `${Math.round(lengthInCm)}cm`;
        }
        return "";
    }

    // User can change before first point has been set
    // After that, command is terminated is viewer mode changes
    onViewerModeChange(): void {
        switch (this.cmdState) {
            case AddLengthDimensionState.SETTING_FIRST_PT:
                break;  
            default:
                this.end();
                break;
        }
    }
}



export default AddLengthDimensionCmd;